import PropTypes from "prop-types";
import React, { Component } from "react";
import { Box } from "@material-ui/core";
import styled from "styled-components";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { bindActionCreators } from "redux";
import { withRouter } from "react-router-dom";
import Calendar from "../../waybee-ui/Calendar/Calendar";
// eslint-disable-next-line import/no-named-as-default
import TeacherService from "../../services/TeacherService";
import WuiClassesList from "../Classes/components/ClassesList";
import WuiCommunicationList from "../CommunicationDashboard/components/CommunicationList";
import CommunicationDialog from "../CommunicationDashboard/components/CommunicationDialog";
import CommunicationService from "../../services/CommunicationService";
import Widget from "../../waybee-ui/Widget";
import Banner from "./components/Banner";
import UserStore from "../../utils/UserStore";
import markAsRead from "../../utils/markAsRead";
import { loadCommunications } from "../../redux/actions";
import { communicationStateType } from "../../types";
import { Can } from "../../acl/can";

/** AppHeader 90px + main padding 20+20 (Toolbar Content) */
const HOME_VIEWPORT_OFFSET = 130;

const ClassesList = styled(WuiClassesList)`
  margin: 0;
  width: 100%;
`;

const CommunicationList = styled(WuiCommunicationList)`
  margin: 0;
  width: 100%;
`;

const HomeRoot = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - ${HOME_VIEWPORT_OFFSET}px);
  max-height: calc(100vh - ${HOME_VIEWPORT_OFFSET}px);
  min-height: 0;
  overflow: hidden;
  box-sizing: border-box;
`;

const HomeRow = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  align-items: stretch;
  gap: 16px;
  @media (max-width: 960px) {
    flex-direction: column;
  }
`;

const LeftCol = styled.div`
  flex: 0 0 25%;
  max-width: 25%;
  min-width: 0;
  min-height: 0;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  @media (max-width: 960px) {
    flex: 0 0 auto;
    max-width: 100%;
    width: 100%;
    min-height: 360px;
  }
`;

const RightCol = styled.div`
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const WidgetsRow = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  gap: 16px;
  margin-top: 24px;
  padding-top: 0;
  align-items: stretch;
  & > * {
    flex: 1;
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  @media (max-width: 960px) {
    flex-direction: column;
  }
`;

const CalendarColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  height: 100%;
`;

const CalendarFrame = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

class Home extends Component {
  constructor(props) {
    super(props);

    this.state = {
      calendarOptions: undefined,
      userInfo: JSON.parse(localStorage.getItem("userExternalInfo")),
      openDialog: false,
      selectedCommunication: null,
      calendarFrameHeight: 0
    };

    this.calendarFrameRef = null;
    this.resizeObserver = null;
  }

  componentDidMount() {
    this.resizeObserver = new ResizeObserver(entries => {
      if (!entries.length) {
        return;
      }
      const h = Math.floor(entries[0].contentRect.height);
      if (h < 64) {
        return;
      }
      this.setState(prev =>
        prev.calendarFrameHeight === h ? null : { calendarFrameHeight: h }
      );
    });
    if (this.calendarFrameRef) {
      this.resizeObserver.observe(this.calendarFrameRef);
    }
    const calendarOptions = this.setOptions();
    this.setState(() => ({ calendarOptions }));
  }

  componentWillUnmount() {
    if (this.resizeObserver) {
      if (this.calendarFrameRef) {
        this.resizeObserver.unobserve(this.calendarFrameRef);
      }
      this.resizeObserver.disconnect();
    }
  }

  setCalendarFrameRef = el => {
    if (this.resizeObserver && this.calendarFrameRef) {
      this.resizeObserver.unobserve(this.calendarFrameRef);
    }
    this.calendarFrameRef = el;
    if (el && this.resizeObserver) {
      this.resizeObserver.observe(el);
    }
  };

  onClickHomeCommunication = async communication => {
    this.setState({
      openDialog: true,
      selectedCommunication: communication
    });

    if (communication.interaction && !communication.interaction.openedAt) {
      const res = await CommunicationService.markAsRead(communication.id);
      markAsRead(res.data);
    }
  };

  setOptions() {
    const { userInfo } = this.state;
    return {
      views: {
        timeGridDay: {
          slotLabelFormat: { hour: "numeric", minute: "2-digit" },
          columnHeaderFormat: {
            weekday: "short",
            day: "numeric",
            month: "long"
          }
        }
      },
      allDaySlot: false,
      events: async (info, successCallback, failureCallback) => {
        try {
          const mock = await TeacherService.getSchedule(
            info.startStr,
            info.endStr,
            userInfo.codPessoa
          );
          successCallback(mock);
        } catch (e) {
          failureCallback(e);
        }
      },
      defaultView: "timeGridDay"
    };
  }

  getMergedCalendarOptions = () => {
    const { calendarOptions, calendarFrameHeight } = this.state;
    if (!calendarOptions) {
      return undefined;
    }
    return {
      ...calendarOptions,
      height: Math.max(200, calendarFrameHeight || 400)
    };
  };

  updateHomeCommunication = communication => {
    const {
      loadCommunications: setCommunications,
      communicationState
    } = this.props;

    const updatedCommunications = communicationState.communications.map(
      comm => {
        if (comm.id === communication.id) return communication;
        return comm;
      }
    );

    setCommunications(updatedCommunications);
    this.setState({ selectedCommunication: communication });
  };

  render() {
    const { openDialog, selectedCommunication, calendarOptions } = this.state;
    const { history, t } = this.props;
    const calendarOptsMerged = this.getMergedCalendarOptions();

    const userName = (UserStore.getUserName() || "").split(" ")[0];

    return (
      <>
        <HomeRoot>
          <HomeRow>
            <Can I="use module" a="diaryMySchedule">
              <LeftCol>
                <CalendarColumn>
                  {calendarOptions && (
                    <CalendarFrame ref={this.setCalendarFrameRef}>
                      <Calendar
                        options={calendarOptsMerged}
                        today
                        fillContainer
                      />
                    </CalendarFrame>
                  )}
                </CalendarColumn>
              </LeftCol>
            </Can>
            <RightCol>
              <Box flexShrink={0}>
                <Banner userName={userName} />
              </Box>
              <WidgetsRow>
                <Can I="use module" a="diaryClasses">
                  <div>
                    <Widget
                      title={t("home.nextClasses")}
                      fill
                      onTitleClick={() => history.push("/classes")}
                    >
                      <ClassesList size="compact" />
                    </Widget>
                  </div>
                </Can>
                <Can I="use module" a="diaryCommunication">
                  <div>
                    <Widget
                      title={t("home.recentComms")}
                      fill
                      onTitleClick={() => history.push("/communication")}
                    >
                      <CommunicationList
                        size="compact"
                        onClick={this.onClickHomeCommunication}
                      />
                    </Widget>
                  </div>
                </Can>
              </WidgetsRow>
            </RightCol>
          </HomeRow>
        </HomeRoot>
        {selectedCommunication && (
          <CommunicationDialog
            open={openDialog}
            communication={selectedCommunication}
            onClose={() => this.setState({ openDialog: false })}
            updateCommunication={this.updateHomeCommunication}
          />
        )}
      </>
    );
  }
}

Home.propTypes = {
  communicationState: communicationStateType.isRequired,
  loadCommunications: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired
  }).isRequired, // injected by withRouter
  t: PropTypes.func.isRequired
};

const mapDispatchToProps = dispatch =>
  bindActionCreators({ loadCommunications }, dispatch);

const mapStateToProps = state => ({
  communicationState: state.communicationState
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(withTranslation()(Home))
);
